import { DppTriageProto_CompletionStatus } from "@noom/noom-contracts/noom_contracts/healthcare/dpp_triage";
import { Api } from "@noom/noomscape";

import { DppTriageSurveyType } from "@/api/dpp/saveTriageData";
import { SurveyAnswers, useAppContext } from "@/contexts";
import { sanitizeDppSurveyAnswers } from "@/utils/dpp";
import { getSanitizedRetriageAnswers } from "@/utils/retriage";

export const useSaveTriageData = (partnerId: number | undefined) => {
  const {
    eligibilityInfo,
    retriageEligibility,
    ssTriageUuid,
    setSsTriageUuid,
  } = useAppContext();

  const getInitialRequest = (surveyAnswers: SurveyAnswers) => {
    const participantId =
      surveyAnswers.eligibilityCheck?.employeeId || eligibilityInfo?.employeeId;
    const eligibilityId = eligibilityInfo?.eligibilityId;

    return {
      survey: sanitizeDppSurveyAnswers(surveyAnswers),
      ...(!!eligibilityId && { eligibilityId }),
      ...(!!participantId && { participantId }),
      ...(!!ssTriageUuid && { resourceKey: ssTriageUuid }),
    };
  };

  // Retriage saves always create a new record and only use retriage
  // eligibility, which is never stale from an earlier enrollment
  const getRetriageRequest = (surveyAnswers: SurveyAnswers) => {
    const participantId = retriageEligibility?.userProfile?.participantId;
    const eligibilityId = retriageEligibility?.eligibilityId;

    return {
      survey: getSanitizedRetriageAnswers(surveyAnswers),
      ...(!!eligibilityId && { eligibilityId }),
      ...(!!participantId && { participantId }),
    };
  };

  const saveTriageData = async (
    surveyAnswers: SurveyAnswers,
    completionStatus: DppTriageProto_CompletionStatus,
    surveyType: DppTriageSurveyType,
  ) => {
    const isRetriage = surveyType === "RETRIAGE";

    const { key } = await Api.call(
      "dpp.saveTriageData",
      Api.api.dpp.saveTriageData,
      {
        completionStatus,
        partnerId,
        surveyType,
        ...(isRetriage
          ? getRetriageRequest(surveyAnswers)
          : getInitialRequest(surveyAnswers)),
      },
    );

    if (!isRetriage && !ssTriageUuid) {
      setSsTriageUuid(key);
    }

    return key;
  };

  return { saveTriageData };
};
